import React, { useCallback, useEffect, useState } from 'react'
import FormTile from 'src/components/common/containers/FormTile'
import RewardConfigTile from 'src/components/rewards/setup/RewardConfigTile'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import { EarnConfigType } from 'src/lib/types/rewards'
import {
  getEarnRewardsPayload,
  generateEarnRuleDialogFooterText,
  getEarnRewardsMinOVPayload,
  generateMinOVDialogFooterText,
  generateEarnRewardsPreviewText
} from 'src/lib/utils/rewards'
import { isEmptyObj } from 'src/lib/utils/helpers'
import EarnRuleDialog from 'src/components/rewards/setup/earn/dialogs/EarnRuleDialog'
import MinOrderValueDialog from 'src/components/rewards/setup/MinOrderValueDialog'
import { ConsoleNetwork } from 'mdi-material-ui'
import { useActivityLogs, EntityName } from 'src/lib/hooks/useActivityLogs'
import { useRewardsPermissions } from 'src/lib/access-permission-hooks/useRewardsPermissions'

interface EarnRewardsProps {
  handleUpdateRewardConfig: (payload: any) => Promise<void>
  rewardsConfig: any
}

const EarnRewards: React.FC<EarnRewardsProps> = ({ handleUpdateRewardConfig, rewardsConfig }) => {
  const { sendActivityLog } = useActivityLogs()
  const [initialEarnConfig, setInitialEarnConfig] = useState<EarnConfigType>({
    earnRatio: 100
  })
  const [earnConfig, setEarnConfig] = useState<EarnConfigType>({ earnRatio: 100 })
  const [initialMinOv, setInitialMinOv] = useState<number>(0)
  const [minOV, setMinOV] = useState<number>(0)
  const [openRuleDialog, setOpenRuleDialog] = useState<boolean>(false)
  const [openMinOVDialog, setOpenMinOVDialog] = useState<boolean>(false)

  const { canEditSetupRewards } = useRewardsPermissions()

  useEffect(() => {
    if (!rewardsConfig || isEmptyObj(rewardsConfig)) return

    const rewards = rewardsConfig as any
    const earnRatio = rewards.event_reward_rule_map?.ORDER_PLACED?.amount
    const expiry = rewards.event_reward_rule_map?.ORDER_PLACED?.expiry_time
    const delay = rewards.order_confirmation_strategy?.ONLINE?.duration_after

    const initialConfig: EarnConfigType = {
      earnRatio: (earnRatio || 0) * 100,
      expiry: expiry ? expiry / 86400000 : null,
      delay: delay ? delay / 86400000 : null
    }

    const initialMinOV: number =
      (rewardsConfig as any).event_reward_rule_map?.ORDER_PLACED?.blocking_conditions?.find(
        (condition: any) => condition.type === 'MIN_CART_VALUE'
      )?.value ?? 0
    setInitialEarnConfig({ ...initialConfig })
    setInitialMinOv(initialMinOV)
    setEarnConfig(initialConfig)
    setMinOV(initialMinOV)
  }, [rewardsConfig])

  const handleEarnRewardsSave = useCallback(async () => {
    const payload = getEarnRewardsPayload(earnConfig)
    try {
      await handleUpdateRewardConfig(payload)
      const activityLogPayload = {
        entityName: EntityName.REWARDS,
        actionName: 'edited the configuration'
      }
      sendActivityLog(activityLogPayload)
    } catch (e) {
      console.error(e)
    } finally {
      setOpenRuleDialog(false)
    }
  }, [earnConfig])

  const handleMinOVSave = useCallback(async () => {
    const payload = getEarnRewardsMinOVPayload(minOV)
    try {
      await handleUpdateRewardConfig(payload)
      const activityLogPayload = {
        entityName: EntityName.REWARDS,
        actionName: 'edited the configuration'
      }
      sendActivityLog(activityLogPayload)
    } catch (e) {
      console.error(e)
    } finally {
      setOpenMinOVDialog(false)
    }
  }, [minOV])

  return (
    <>
      <FormTile title={'Earn on purchase'} tooltip={''}>
        <ul className='flex flex-col rounded-lg divide-y-1 border-1 border-gray-light divide-gray-light'>
          <li
            onClick={() => setOpenRuleDialog(true && canEditSetupRewards)}
            key={'earn-on-purchase-rule'}
            className='rounded-t-[inherit]'
          >
            <RewardConfigTile
              header={<>{generateEarnRewardsPreviewText(initialEarnConfig)}</>}
              subHeader={<>Earn on purchase</>}
            />
          </li>
          <li
            key={'earn-on-purchase-condition-min_ov'}
            onClick={() => setOpenMinOVDialog(true && canEditSetupRewards)}
            className='rounded-b-[inherit]'
          >
            <RewardConfigTile
              header={<>{`₹${Boolean(initialMinOv) ? initialMinOv : 0}`}</>}
              subHeader={<>Minimum order value for earn</>}
            />
          </li>
        </ul>
      </FormTile>

      <FloGenericDialog
        width={800}
        isOpen={openRuleDialog}
        handleClose={() => setOpenRuleDialog(false)}
        content={
          <EarnRuleDialog
            setEarnConfig={values => setEarnConfig(oldValues => ({ ...oldValues, ...values }))}
            earnConfig={earnConfig}
            initialEarnConfig={{ ...initialEarnConfig }}
          />
        }
        referrer='rewards-change-rule'
        title={'Set cashback rule'}
        jobButtonText={'Save'}
        handleJob={async () => {
          handleEarnRewardsSave()
        }}
        footerTxt={generateEarnRuleDialogFooterText(earnConfig)}
      />

      <FloGenericDialog
        width={800}
        isOpen={openMinOVDialog}
        handleClose={() => setOpenMinOVDialog(false)}
        content={<MinOrderValueDialog setMinOV={setMinOV} minOV={minOV} txnType='EARN' initialMinOv={initialMinOv} />}
        referrer='rewards-min_ov-rule'
        title={'Set minimum order value for earn'}
        jobButtonText={'Save'}
        handleJob={async () => {
          handleMinOVSave()
        }}
        footerTxt={generateMinOVDialogFooterText(minOV, 'EARN')}
      />
    </>
  )
}

export default React.memo(EarnRewards)
